import { roundToPlaces } from "../../utils/roundToPlaces.js";
import type { Order } from "../Order.js";
import type { OrderCreateInputUnion } from "../OrderCreateInputUnion.js";
import type { OrderPrices, OrderPricesWithUnknownDeliveryPrice } from "../OrderPrices.js";
import type { PriceLevel } from "../PriceLevel.js";
import { TaxLevel } from "../TaxLevel.js";
import type { DeliveryDestination } from "../deliveryDestination.js";
import type { DeliveryDetails } from "../deliveryInfoPerZoneMap.js";
import { calculatePriceWithoutTax } from "./calculatePriceWithoutTax.js";
import { getDeliveryTaxLevel } from "./getDeliveryTaxLevel.js";
import { getPriceOfDelivery } from "./getPriceOfDelivery.js";
import { getPriceOfOrderProduct } from "./getPriceOfOrderProduct.js";
import { getSpreadProducts } from "./getSpreadProducts.js";
import { getSpreadVouchers } from "./getSpreadVouchers.js";
import { hasKnownDeliveryPrice } from "./hasKnownDeliveryPrice.js";

type OrderInput =
	Pick<Order, "products" | "vouchers" | "customerRequirement" | "surcharges"> | Pick<OrderCreateInputUnion, "products">;

export function getPriceOfOrder(input: OrderInput, priceLevel: PriceLevel, destination: null): OrderPrices;
export function getPriceOfOrder(
	input: OrderInput,
	priceLevel: PriceLevel,
	destination: DeliveryDestination | null,
	fixedValues?: {
		fixedDeliveryPrice?: OrderPrices["deliveryPrice"];
		customDeliveryDetails?: DeliveryDetails;
	},
): OrderPricesWithUnknownDeliveryPrice;
export function getPriceOfOrder(
	input: OrderInput,
	priceLevel: PriceLevel,
	destination: DeliveryDestination | null,
	fixedValues?: {
		fixedDeliveryPrice?: OrderPrices["deliveryPrice"];
		customDeliveryDetails?: DeliveryDetails;
	},
): OrderPricesWithUnknownDeliveryPrice {
	let withTax = 0;
	let withoutTax = 0;

	/*
	 * Spread products before calculating the price, otherwise price is not calculated precisely.
	 * Random UUID is not important in this context.
	 */
	const spreadProducts = getSpreadProducts(input.products, () => "");
	const spreadVouchers = getSpreadVouchers("vouchers" in input ? input.vouchers : [], () => "");

	for (const product of spreadProducts) {
		const { withTax: productWithTax, withoutTax: productWithoutTax } = getPriceOfOrderProduct(product);
		withTax += productWithTax;
		withoutTax += productWithoutTax;
	}

	for (const voucher of spreadVouchers) {
		withTax += voucher.orderVoucherProduct.value * voucher.quantity;
		withoutTax += voucher.orderVoucherProduct.value * voucher.quantity;
	}

	if ("customerRequirement" in input && input.customerRequirement) {
		withTax += input.customerRequirement.price;
		withoutTax += calculatePriceWithoutTax(input.customerRequirement.price, TaxLevel.TaxLevel12);
	}

	if ("surcharges" in input && input.surcharges !== undefined) {
		if (input.surcharges.lateOrderSurcharge !== undefined) {
			withTax += input.surcharges.lateOrderSurcharge.withTax;
			withoutTax += input.surcharges.lateOrderSurcharge.withoutTax;
		}
	}

	let deliveryPrice: OrderPricesWithUnknownDeliveryPrice["deliveryPrice"] = 0;
	let deliveryPriceWithoutTax: OrderPricesWithUnknownDeliveryPrice["deliveryPriceWithoutTax"] = 0;
	const deliveryTaxLevel = getDeliveryTaxLevel(input.products);

	if (destination !== null && deliveryTaxLevel !== null) {
		if (fixedValues?.fixedDeliveryPrice !== undefined) {
			deliveryPrice = fixedValues.fixedDeliveryPrice;
			deliveryPriceWithoutTax = calculatePriceWithoutTax(fixedValues.fixedDeliveryPrice, deliveryTaxLevel);
		} else if (withTax > 0) {
			const price = getPriceOfDelivery(deliveryTaxLevel, withTax, destination, fixedValues?.customDeliveryDetails);
			if (price === null) {
				deliveryPrice = null;
				deliveryPriceWithoutTax = null;
			} else if (price === "unreachable") {
				deliveryPrice = "unreachable";
				deliveryPriceWithoutTax = "unreachable";
			} else {
				deliveryPrice = price.withTax;
				deliveryPriceWithoutTax = price.withoutTax;
			}
		}
	}

	const deliveryPriceObject = { deliveryPrice, deliveryPriceWithoutTax };
	if (hasKnownDeliveryPrice(deliveryPriceObject)) {
		withTax += deliveryPriceObject.deliveryPrice;
		withoutTax += deliveryPriceObject.deliveryPriceWithoutTax;
	}

	return {
		deliveryPrice,
		deliveryPriceWithoutTax,
		totalPrice: {
			priceLevel,
			withoutTax: roundToPlaces(withoutTax, 2),
			withTax: roundToPlaces(withTax, 2),
		},
	};
}
